import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { jobsAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { Briefcase, MapPin, Calendar, FileText, List, Settings, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { toast } from 'react-toastify';

const Page = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
  display: grid;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Title = styled.h1`
  margin: 0;
  color: ${p => p.theme.colors.text.primary};
  font-size: 2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${p => p.theme.colors.text.secondary};
  font-size: 0.95rem;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${p => p.theme.colors.border.light};
  border-radius: 12px;
  padding: 2rem;
  display: grid;
  gap: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const Section = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid ${p => p.theme.colors.border.light};
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: ${p => p.theme.colors.text.primary};
  font-size: 1.1rem;
  font-weight: 600;
`;

const SectionIcon = styled.div`
  color: ${p => p.theme.colors.primary};
  display: flex;
  align-items: center;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const FormRowGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: ${p => p.theme.colors.text.primary};
  font-size: 0.9rem;
`;

const LabelText = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const Required = styled.span`
  color: #ef4444;
  font-weight: 600;
`;

const HelpText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${p => p.theme.colors.primary}10;
  border-left: 3px solid ${p => p.theme.colors.primary};
  border-radius: 4px;
  font-size: 0.85rem;
  color: ${p => p.theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const Input = styled.input`
  border: 1.5px solid ${p => p.theme.colors.border.light};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  color: ${p => p.theme.colors.text.primary};
  background: #fff;
  transition: all 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }

  &:disabled {
    background: ${p => p.theme.colors.background.secondary};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${p => p.theme.colors.text.tertiary};
  }
`;

const Textarea = styled.textarea`
  border: 1.5px solid ${p => p.theme.colors.border.light};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  color: ${p => p.theme.colors.text.primary};
  background: #fff;
  transition: all 0.2s ease;
  width: 100%;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }

  &::placeholder {
    color: ${p => p.theme.colors.text.tertiary};
  }
`;

const Select = styled.select`
  border: 1.5px solid ${p => p.theme.colors.border.light};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  color: ${p => p.theme.colors.text.primary};
  background: #fff;
  transition: all 0.2s ease;
  width: 100%;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: ${p => p.theme.colors.background.secondary};
  border-radius: 8px;
  border: 1.5px solid ${p => p.theme.colors.border.light};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${p => p.theme.colors.background.tertiary};
    border-color: ${p => p.theme.colors.primary};
  }

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: ${p => p.theme.colors.primary};
  }

  label {
    cursor: pointer;
    font-weight: 500;
    color: ${p => p.theme.colors.text.primary};
    flex: 1;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid ${p => p.theme.colors.border.light};
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 120px;
  justify-content: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  color: #fff;
  background: ${p => p.theme.colors.primary};

  &:hover:not(:disabled) {
    background: ${p => p.theme.colors.primary}dd;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${p => p.theme.colors.primary}40;
  }
`;

const SecondaryButton = styled(Button)`
  color: ${p => p.theme.colors.text.primary};
  background: ${p => p.theme.colors.background.secondary};
  border: 1.5px solid ${p => p.theme.colors.border.medium};

  &:hover:not(:disabled) {
    background: ${p => p.theme.colors.background.tertiary};
    border-color: ${p => p.theme.colors.border.dark};
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

const EMPLOYMENT_TYPES = [
  { value: '', label: 'Select employment type...' },
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'internship', label: 'Internship' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'volunteer', label: 'Volunteer' },
];

const initial = {
  title: '',
  summary: '',
  description: '',
  requirements: '',
  location: '',
  employment_type: '',
  deadline: '',
  is_active: true,
  display_order: 0,
};

const JobCreate = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const onCheck = (e) => {
    const { name, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: checked }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await jobsAPI.create(form);
      toast.success('Job created successfully!');
      navigate('/app/jobs');
    } catch (e) {
      const errorMessage = e.response?.data?.message || 'Failed to create job. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page theme={theme}>
      <Header>
        <Title>
          <Briefcase size={28} />
          Create Job Posting
        </Title>
        <Subtitle>
          Fill in the details below to create a new job posting. All fields marked with <Required>*</Required> are required.
        </Subtitle>
      </Header>

      <Card theme={theme}>
        <form onSubmit={save}>
          {/* Basic Information Section */}
          <Section>
            <SectionHeader>
              <SectionIcon>
                <FileText size={20} />
              </SectionIcon>
              <SectionTitle>Basic Information</SectionTitle>
            </SectionHeader>
            <FormGrid>
              <FormRow>
                <Label>
                  <LabelText>
                    Job Title <Required>*</Required>
                  </LabelText>
                </Label>
                <Input
                  name="title"
                  required
                  value={form.title}
                  onChange={onChange}
                  placeholder="e.g., Senior Software Engineer"
                />
              </FormRow>

              <FormRow>
                <Label>
                  <LabelText>
                    Summary
                  </LabelText>
                </Label>
                <Input
                  name="summary"
                  value={form.summary}
                  onChange={onChange}
                  placeholder="Brief summary of the position (optional)"
                />
                <HelpText>
                  <Info size={14} />
                  A short summary that will appear in job listings
                </HelpText>
              </FormRow>

              <FormRow>
                <Label>
                  <LabelText>
                    Description <Required>*</Required>
                  </LabelText>
                </Label>
                <Textarea
                  name="description"
                  required
                  rows={6}
                  value={form.description}
                  onChange={onChange}
                  placeholder="Provide a detailed description of the job position, responsibilities, and what makes this role exciting..."
                />
              </FormRow>

              <FormRow>
                <Label>
                  <LabelText>
                    Requirements
                  </LabelText>
                </Label>
                <Textarea
                  name="requirements"
                  rows={5}
                  value={form.requirements}
                  onChange={onChange}
                  placeholder="List the required qualifications, skills, experience, and education..."
                />
              </FormRow>
            </FormGrid>
          </Section>

          {/* Job Details Section */}
          <Section>
            <SectionHeader>
              <SectionIcon>
                <Settings size={20} />
              </SectionIcon>
              <SectionTitle>Job Details</SectionTitle>
            </SectionHeader>
            <FormGrid>
              <FormRowGrid>
                <FormRow>
                  <Label>
                    <LabelText>
                      <MapPin size={16} />
                      Location
                    </LabelText>
                  </Label>
                  <Input
                    name="location"
                    value={form.location}
                    onChange={onChange}
                    placeholder="e.g., Remote, New York, NY"
                  />
                </FormRow>

                <FormRow>
                  <Label>
                    <LabelText>
                      <Briefcase size={16} />
                      Employment Type
                    </LabelText>
                  </Label>
                  <Select
                    name="employment_type"
                    value={form.employment_type}
                    onChange={onChange}
                  >
                    {EMPLOYMENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                </FormRow>
              </FormRowGrid>

              <FormRowGrid>
                <FormRow>
                  <Label>
                    <LabelText>
                      <Calendar size={16} />
                      Application Deadline
                    </LabelText>
                  </Label>
                  <Input
                    type="date"
                    name="deadline"
                    value={form.deadline}
                    onChange={onChange}
                  />
                </FormRow>

                <FormRow>
                  <Label>
                    <LabelText>
                      <List size={16} />
                      Display Order
                    </LabelText>
                  </Label>
                  <Input
                    type="number"
                    name="display_order"
                    value={form.display_order}
                    onChange={onChange}
                    min="0"
                    placeholder="0"
                  />
                  <HelpText>
                    <Info size={14} />
                    Lower numbers appear first in job listings (e.g., 1, 2, 3...)
                  </HelpText>
                </FormRow>
              </FormRowGrid>
            </FormGrid>
          </Section>

          {/* Status Section */}
          <Section>
            <SectionHeader>
              <SectionIcon>
                <CheckCircle2 size={20} />
              </SectionIcon>
              <SectionTitle>Status</SectionTitle>
            </SectionHeader>
            <CheckboxWrapper onClick={() => setForm(prev => ({ ...prev, is_active: !prev.is_active }))}>
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={onCheck}
              />
            <label>
                Active - This job posting will be visible to applicants
            </label>
            </CheckboxWrapper>
          </Section>

          {error && (
            <ErrorMessage>
              <AlertCircle size={18} />
              {error}
            </ErrorMessage>
          )}

          <Actions>
            <SecondaryButton
              theme={theme}
              type="button"
              onClick={() => navigate('/app/jobs')}
              disabled={saving}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              theme={theme}
              type="submit"
              disabled={saving}
            >
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <CheckCircle2 size={18} />
                  Create Job
                </>
              )}
            </PrimaryButton>
          </Actions>
        </form>
      </Card>
    </Page>
  );
};

export default JobCreate;
